"use client";

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore
} from "react";
import { fireErrorDisplayed } from "../../services/analytics";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * CompareTray state carrier.
 *
 * The tray and every per-card checkbox read the same selection. A plain lift
 * would re-render every card on each toggle; this external store lets a card
 * subscribe to a slice (`selectedItems.includes(id)`) and re-render only when
 * THAT card's membership flips.
 *
 * ⚠ Deliberate divergence from the DDS CLAUDE.md "do not reach for Context"
 * rule. The rule's rationale ("trees here are shallow by design") does not
 * apply: the selection is read AND written across a card grid, a sibling tray
 * and the tray's own body. Documented in design-conflicts/compare-tray.mdx.
 */

export interface CompareTrayContextValue {
  selectedItems: readonly string[];
  trayOpen: boolean;
  collapsedTray: boolean;
  isMaxSelected: boolean;
  maxSelectedTags: number;
  updateSelectedTag: (tag: string) => void;
  setTrayOpen: (open: boolean) => void;
  updateCollapsedTray: (collapsed: boolean) => void;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
}

interface CompareState {
  /* `readonly` guards the invariant that selectedItems is REPLACED (spread or
     filter), never mutated. A `.push()` would change the array in place with
     an unchanged reference, so no subscriber would re-render and the UI would
     silently go stale. */
  selectedItems: readonly string[];
  trayOpen: boolean;
  collapsedTray: boolean;
  isMaxSelected: boolean;
}

export interface CompareTrayStore {
  subscribe: (listener: () => void) => () => void;
  getState: () => CompareState;
  getSnapshot: () => CompareTrayContextValue;
  updateSelectedTag: (tag: string) => void;
  setTrayOpen: (open: boolean) => void;
  updateCollapsedTray: (collapsed: boolean) => void;
  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
}

const CompareTrayContext = createContext<CompareTrayStore | undefined>(undefined);

export interface CompareTrayProviderProps {
  children: ReactNode;
  /** Upper bound on simultaneous selections. Defaults to 3. Non-integer or
   *  non-positive values fall back to 3. */
  maxSelectedTags?: number;
  /** Pre-selected tags, applied once on first mount — no analytics, no tray
   *  opening. Pre-ticked `compare_checked` cards flow through this, which
   *  avoids the hydration flip and the max-limit beacon firing on page load. */
  initialSelected?: readonly string[];
}

/* Positive-integer-or-fallback. A raw unvalidated `-1` from a host without
   an adapter would otherwise reject every tick (`length < -1 || alreadySelected`
   is false), and 0 would reject the first tick too. */
function normalizeCap(value: number | undefined): number {
  const n = Number(value);
  return Number.isInteger(n) && n >= 1 ? n : 3;
}

function CompareTrayProviderImpl({
  children,
  maxSelectedTags,
  initialSelected
}: CompareTrayProviderProps) {
  const triggerElementRef = useRef<HTMLElement | null>(null);

  /* configRef keeps the latest cap accessible from the store (created once)
     without re-creating the store when the prop changes. */
  const configRef = useRef({
    maxSelectedTags: normalizeCap(maxSelectedTags)
  });
  configRef.current = {
    maxSelectedTags: normalizeCap(maxSelectedTags)
  };

  const storeRef = useRef<CompareTrayStore>();
  if (!storeRef.current) {
    /* Seed from `initialSelected` once at construction so the server render
       and the client first-render already carry the selection — no
       post-hydration flip, no `error_displayed` on page load. Capped and
       deduped to match the invariant `updateSelectedTag` holds. */
    const seedSet = new Set<string>();
    const seed: string[] = [];
    const seedCap = configRef.current.maxSelectedTags;
    for (const raw of initialSelected ?? []) {
      const t = typeof raw === "string" ? raw.trim().toLowerCase() : "";
      if (t && !seedSet.has(t) && seed.length < seedCap) {
        seedSet.add(t);
        seed.push(t);
      }
    }
    let state: CompareState = {
      selectedItems: seed,
      trayOpen: false,
      collapsedTray: false,
      isMaxSelected: false
    };
    const listeners = new Set<() => void>();
    const emit = () => listeners.forEach(l => l());

    const setState = (patch: Partial<CompareState>) => {
      /* Skip the sweep when the patch changes nothing — the slice guards
         would bail re-renders anyway, but short-circuit avoids wasted notify. */
      const changed = (Object.keys(patch) as (keyof CompareState)[]).some(
        key => !Object.is(state[key], patch[key])
      );
      if (!changed) {
        return;
      }
      state = {
        ...state,
        ...patch
      };
      emit();
    };

    /* Normalise inside the store so EVERY entry point — the chip onClick,
       the card checkbox, a direct `store.updateSelectedTag("Choice")` from a
       harness or a future host — lands on the same slice. Edge-level
       normalisation (R4) left harnesses able to store mixed case. */
    const normalize = (tag: string): string => (typeof tag === "string" ? tag.trim().toLowerCase() : "");

    const updateSelectedTag = (rawTag: string) => {
      const { maxSelectedTags: cap } = configRef.current;
      const tag = normalize(rawTag);
      if (tag === "") {
        setState({
          selectedItems: [],
          isMaxSelected: false
        });
        return;
      }
      const alreadySelected = state.selectedItems.includes(tag);
      if (state.selectedItems.length < cap || alreadySelected) {
        const selectedItems = alreadySelected ?
          state.selectedItems.filter(item => item !== tag) :
          [...state.selectedItems, tag];
        setState({
          selectedItems,
          isMaxSelected: false
        });
      } else {
        setState({ isMaxSelected: true });
        /* Mirrors the source helper's `fireErrorDisplayedGA` call — DDS's
           `services/analytics.ts` publishes `fireErrorDisplayed` for exactly
           this GA row, so the port keeps the event shape rather than
           repurposing a link_click. */
        fireErrorDisplayed({
          errorCode: "MAX_COMPARE_LIMIT",
          errorMessage: `Maximum of ${cap} packages can be compared`
        });
      }
    };

    const setTrayOpen = (open: boolean) => setState({ trayOpen: open });
    const updateCollapsedTray = (collapsed: boolean) => setState({ collapsedTray: collapsed });

    const buildSnapshot = (): CompareTrayContextValue => ({
      selectedItems: state.selectedItems,
      trayOpen: state.trayOpen,
      collapsedTray: state.collapsedTray,
      isMaxSelected: state.isMaxSelected,
      maxSelectedTags: configRef.current.maxSelectedTags,
      updateSelectedTag,
      setTrayOpen,
      updateCollapsedTray,
      triggerElementRef
    });

    storeRef.current = {
      subscribe: (listener: () => void) => {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      getState: () => state,
      /* Fresh snapshot each call so config stays current; full consumers use
         shallowEqual (per-field Object.is) so a fresh object with unchanged
         fields does not trigger a re-render. */
      getSnapshot: () => buildSnapshot(),
      updateSelectedTag,
      setTrayOpen,
      updateCollapsedTray,
      triggerElementRef
    };
  }

  return (
    <CompareTrayContext.Provider value={storeRef.current}>
      {children}
    </CompareTrayContext.Provider>
  );
}
export const CompareTrayProvider = withSafeRender(CompareTrayProviderImpl, "CompareTrayProvider");

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) {
    return true;
  }
  if (
    typeof a !== "object" ||
    a === null ||
    typeof b !== "object" ||
    b === null
  ) {
    return false;
  }
  const keysA = Object.keys(a as object);
  const keysB = Object.keys(b as object);
  if (keysA.length !== keysB.length) {
    return false;
  }
  return keysA.every(key => Object.is(
    (a as Record<string, unknown>)[key],
    (b as Record<string, unknown>)[key]
  ));
}

/**
 * Subscribe to a slice of the compare store and re-render only when that slice
 * changes. The last slice is cached and returned while `isEqual` holds, so a
 * selector that builds a fresh object (the full snapshot) stays referentially
 * stable — `useSyncExternalStore` requires it, or it re-renders forever.
 */
function useStoreSlice<T>(
  store: CompareTrayStore | undefined,
  select: (store: CompareTrayStore) => T,
  fallback: T,
  isEqual: (a: T, b: T) => boolean = Object.is
): T {
  const cache = useRef<{ value: T } | null>(null);
  /* Stable per store, so React does not resubscribe on every render. */
  const subscribe = useCallback(
    (listener: () => void) => (store ? store.subscribe(listener) : () => undefined),
    [store]
  );
  const getSnapshot = (): T => {
    const next = store ? select(store) : fallback;
    if (cache.current && isEqual(cache.current.value, next)) {
      return cache.current.value;
    }
    cache.current = { value: next };
    return next;
  };
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** Full snapshot — for the tray itself. Throws outside a provider. */
export function useCompareTray(): CompareTrayContextValue {
  const store = useContext(CompareTrayContext);
  if (!store) {
    throw new Error(
      "useCompareTray must be used within a CompareTrayProvider"
    );
  }
  return useStoreSlice<CompareTrayContextValue>(
    store,
    s => s.getSnapshot(),
    undefined as unknown as CompareTrayContextValue,
    shallowEqual
  );
}

/**
 * Narrow selector — subscribes ONLY to whether `tag` is currently selected.
 * A per-card checkbox using this re-renders only when its own membership
 * flips, not when other cards toggle. Returns false outside a provider.
 */
export function useIsCompareSelected(tag: string): boolean {
  const store = useContext(CompareTrayContext);
  return useStoreSlice(
    store,
    s => s.getState().selectedItems.includes(tag),
    false
  );
}

/** Narrow selector — whether the tray is open. False outside a provider. */
export function useIsCompareTrayOpen(): boolean {
  const store = useContext(CompareTrayContext);
  return useStoreSlice(store, s => s.getState().trayOpen, false);
}

/**
 * Returns the store handle for imperative actions. The store identity is
 * created once per provider and never changes, so reading it never triggers
 * a re-render. Returns undefined outside a provider.
 */
export function useCompareTrayStore(): CompareTrayStore | undefined {
  return useContext(CompareTrayContext);
}
